import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {get} from 'lodash'
import i18next from 'i18next';

import {
  getIsMobile,
  getLanguage,
  removeToken,
  removeUserZipcode,
  setLanguage,
  setToken,
  setIsStaff,
  setUserZipcode
} from '@/common';
import {
  SHIPPING_ADDRESS_KEY,
  DEFAULT_ZIPCODE,
  LANGUAGE_CHINESE_KEY,
  LANGUAGE_ENGLISH_KEY,
} from '@/constants';
import {
  getApiTokenAuth,
  getCustomerOao,
  getProductGroup
} from '@/api/req-api'

export const userLogin = createAsyncThunk(
  'common/login',
  async ({username, password}, {rejectWithValue}) => {
    try {
      const response = await getApiTokenAuth({
        username,
        password
      }); 
      setToken(response.data.token);
      setIsStaff(`${response.data.is_staff}`);
      return get(response, 'data');
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
export const fetchUserInfo = createAsyncThunk(
  'common/userInfo',
  async (_, {rejectWithValue}) => {
    try {
      const response = await getCustomerOao();
      return get(response, 'data');
    } catch (e) {
      if (e?.message === "You are not a registered user") {
        // Return a "fake" payload so this goes through the fulfilled reducer
        return {
           IsInternal: true,
           IsSCM: true,
           Customer_Detail: [],
         };
      } else {
        // Other errors = rejected
        removeToken();
        return rejectWithValue(e);
      }
    }
  },
);

export const fetchProductGroup = createAsyncThunk(
    'common/productGroup',
    async (_, {rejectWithValue}) => {
      try {
        const response = await getProductGroup();
        return get(response, 'data');
      } catch (e) {
        return rejectWithValue(e);
      }
    },
)

const commonSlice = createSlice({
  name: 'common',
  initialState: {
    isMobile: getIsMobile(),
    locale: getLanguage(),
    loading: false,
    isLogged: false,
    isInternal: false,
    isSCM: false,
    userInfo: null,
    customerDetail: [],
    billingAddress: null,
    productGroupMap: {},
    productCategoryMap: {},
    productGroupMenu: [],
    availableCredit: 0,
    zipcode: DEFAULT_ZIPCODE
  },
  reducers: {
    startLoading(state) {
      state.loading = true;
    },
    stopLoading(state) {
      state.loading = false;
    },
    changeLanguage(state) {
      const language = state.locale === LANGUAGE_ENGLISH_KEY ? LANGUAGE_CHINESE_KEY : LANGUAGE_ENGLISH_KEY
      i18next.changeLanguage(language).then(() => {})
      setLanguage(language)
      state.locale = language
    },
    setIsLogged(state, {payload}) {
      state.isLogged = payload;
    },
    setIsInternal(state, {payload}) {
      state.isInternal = payload;
    },
    setIsSCM(state, {payload}) {
      state.isSCM = payload;
    },
    logout(state) {
      state.isLogged = false
      state.isInternal = false
      state.isSCM = false
      state.userInfo = null
      state.customerDetail = []
      state.billingAddress = null
      state.zipcode = DEFAULT_ZIPCODE
      state.availableCredit = 0
      removeUserZipcode()
      removeToken()
    }
  },
  extraReducers: builder => {
    builder.addCase(userLogin.fulfilled, (state, {payload}) => {
      state.isLogged = true
      setToken(payload.token)
      setIsStaff(`${payload.is_staff}`);
    })
    builder.addCase(fetchUserInfo.fulfilled, (state, {payload}) => {
      state.userInfo = payload
      state.isInternal = payload.IsInternal
      state.isSCM = payload.IsSCM || false
      const customerDetail = payload?.Customer_Detail || []
      state.customerDetail = customerDetail
      const shippingAddress = customerDetail.find(item => item.Type === SHIPPING_ADDRESS_KEY)
      state.shippingAddress = shippingAddress
      const zipcode = shippingAddress?.Zip || DEFAULT_ZIPCODE
      state.zipcode = zipcode
      state.availableCredit = payload.AvailableCredit
      setUserZipcode(zipcode)
    })
    builder.addCase(fetchUserInfo.rejected, (state, action) => {
      // call logout reducer logic directly
      state.isLogged = false
      state.isInternal = false
      state.isSCM = false
      state.userInfo = null
      state.customerDetail = []
      state.billingAddress = null
      state.zipcode = DEFAULT_ZIPCODE
      state.availableCredit = 0
      removeUserZipcode()
      removeToken()
    })
    builder.addCase(fetchProductGroup.fulfilled, (state, {payload}) => {
      const group = {}, category = {}, menu = []
      for (let i = 0; i < (payload || []).length; i++) {
        const groupRow = payload[i]
        group[groupRow.ProductGroup_id] = groupRow
        const groupCategories = groupRow.Categories ?? []
        const childrenItem = [];
        for (let j = 0; j < groupCategories.length; j++) {
          const cateRow = groupCategories[j];
          category[cateRow.ProductCategory_id] = {...cateRow, ProductGroup_id: groupRow.ProductGroup_id}
          childrenItem.push({
            label: cateRow.Name,
            key: String(cateRow.ProductCategory_id),
            description:cateRow.Description,
            name:cateRow.Name,
            image: cateRow.Image_Url,
          })
        }
        menu.push({
          description:groupRow.Description,
          name:groupRow.Name,
          label: groupRow.Name,
          key: groupRow.Name,
          image: groupRow.Image,
          children: childrenItem,
          isEnable: groupRow.IsEnabled,
          id: groupRow.ProductGroup_id,
        })
      }
      state.productGroupMap = group
      state.productCategoryMap = category
      state.productGroupMenu = menu
    })
  },
});

export const {startLoading, stopLoading, changeLanguage, setIsLogged, setIsInternal, setIsSCM, logout} =
  commonSlice.actions;

export default commonSlice.reducer;
